import React from "react";
import { Alert } from "antd";

import { ProjectSelector } from "./ProjectSelector";
import { PhaseSelector } from "./PhaseSelector";
import { BlockSelector } from "./BlockSelector";
import { StageSelector } from "./StageSelector";
import {
  ProjectSelectionController,
  ProjectSelectionState,
} from "../model/types/ProjectSelection";
import { useProjectsTree } from "../hooks/useProjectsTree";
import { useProjectFormController } from "../hooks/useProjectFormController";

interface ProjectFormContainerProps {
  disabled?: boolean;
  mode?: "create" | "edit";
  selection: ProjectSelectionController;
  onSelectionChange?: (selection: ProjectSelectionState) => void;
  onValidityChange?: (isValid: boolean) => void;
}

function useDebouncedValue<T>(value: T, delay = 150): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

export const ProjectForm: React.FC<ProjectFormContainerProps> = ({
  disabled = false,
  mode = "create",
  selection,
  onSelectionChange,
  onValidityChange,
}) => {
  const [search, setSearch] = React.useState("");
  const debouncedSearch = useDebouncedValue(search, 150);

  const isEditMode = mode === "edit";

  const projectsState = useProjectsTree({
    search: debouncedSearch,
    enabled: !disabled && !isEditMode,
  });
  const { loadProjectById } = projectsState;

  const { cascaded } = useProjectFormController({
    projects: projectsState.projects,
    selection,
    onSelectionChange,
    onValidityChange,
  });

  React.useEffect(() => {
    const selected = selection.state.project;
    if (!selected?.id) return;

    let cancelled = false;

    (async () => {
      const node = await loadProjectById(selected.id);
      if (cancelled || !node) return;

      if (selected.label !== node.label) {
        selection.setProject({ id: node.id, label: node.label });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadProjectById, selection, selection.state.project?.id]);

  return (
    <div className="form-project">
      {projectsState.error && (
        <Alert
          type="error"
          message="Failed to fetch projects"
          description={projectsState.error}
          showIcon
        />
      )}

      <ProjectSelector
        projects={projectsState.projects}
        value={selection.state.project}
        disabled={disabled || isEditMode}
        loadingMore={projectsState.loadingMore}
        hasNextPage={projectsState.hasNext}
        loadNextPage={projectsState.loadMore}
        onSearch={setSearch}
        onChange={selection.setProject}
      />

      <PhaseSelector
        phases={cascaded.phases}
        value={selection.state.phase}
        disabled={
          disabled || !selection.hasProject || cascaded.phases.length === 0
        }
        onChange={selection.setPhase}
      />

      <BlockSelector
        blocks={cascaded.blocks}
        value={selection.state.block}
        disabled={
          disabled || !selection.hasPhase || cascaded.blocks.length === 0
        }
        onChange={selection.setBlock}
      />

      <StageSelector
        stages={cascaded.stages}
        value={selection.state.stage}
        disabled={
          disabled || !selection.hasBlock || cascaded.stages.length === 0
        }
        onChange={selection.setStage}
      />
    </div>
  );
};
